<template>
  <BaseBody>
    <BaseTopBar>
      <BaseTopBarContent className="left col-4">
        {{ $t('sidebar.chartofaccount') }}
      </BaseTopBarContent>
      <BaseTopBarContent
        v-if="hasRoleEdit"
        className="right">
        <div class="action">
          <BaseButton @click="openModalManageAccounts">
            {{ $t('apps.accounting.button.manageaccounts') }}
          </BaseButton>
        </div>
      </BaseTopBarContent>
    </BaseTopBar>
    <BaseContent :loading="loading">
      <div class="d-flex flex-column">
        <div class="d-flex justify-content-between align-items-center mb-1">
          <strong>
            {{ $t('apps.accounting.message.account_name') }}
          </strong>
          <strong>
            {{ $t('apps.accounting.message.account_balance') }}
          </strong>
        </div>
        <TreeItem
          :nodes="viewChartOfAccounts"
        />
      </div>

      <b-modal
        id="modal-manage-accounts"
        :title="$t('apps.accounting.message.manageaccount')"
        size="lg"
        hideFooter
        noCloseOnBackdrop
        noCloseOnEsc
        noFade
      >
        <div class="row">
          <div
            class="col"
            style="max-height: 80vh;overflow-y: auto;">
            <TreeItem
              :master="manageChartOfAccounts"
              :nodes="manageChartOfAccounts"
              :isManage="true"
              @onClickNode="handleClickNode"
              @onAddGroup="handleAddGroup"
              @onAddAccount="handleAddAccount"
              @onToggleNode="handleToggleNode"
            />
          </div>
          <div
            v-if="actionType"
            class="col-md-12 col-lg-5 form">
            <div class="form-row">
              <div class="col-12 mb-2">
                <strong v-if="actionType == 'add_group'">
                  + {{ $t('apps.accounting.message.addgroup') }}
                  <i class="far fa-folder"/>
                </strong>
                <strong v-if="actionType == 'add_account'">
                  + {{ $t('apps.accounting.message.addaccount') }}
                  <i class="far fa-file-alt"/>
                </strong>
                <strong v-if="actionType == 'edit_account'">
                  {{ $t('apps.accounting.message.editaccount') }}
                  <i class="fas fa-edit"/>
                </strong>
              </div>
              <div
                v-if="actionType == 'add_account' || actionType == 'add_group'"
                class="col-md-12 mb-3">
                <input
                  :value="`${form.parent_account_code} - ${form.parent_account_name}`"
                  :disabled="true"
                  class="form-control form-control-sm">
              </div>
              <div class="col-md-4">
                <label>
                  {{ $t('apps.accounting.label.accountcode') }}
                </label>
                <input
                  v-model="$v.form.account_code.$model"
                  :disabled="selectedNode.deletable == false"
                  :class="[
                    { 'is-invalid': $v.form.account_code.$error }
                  ]"
                  class="form-control form-control-sm"
                >
              </div>
              <div class="col-md-8">
                <label>
                  {{ $t('apps.accounting.label.accountname') }}
                </label>
                <input
                  v-model="$v.form.account_name.$model"
                  :disabled="selectedNode.deletable == false"
                  :class="[
                    { 'is-invalid': $v.form.account_name.$error }
                  ]"
                  class="form-control form-control-sm"
                >
              </div>
              <div
                v-if="actionType == 'add_account'"
                class="col-md-12 mt-3">
                <BaseCheckbox
                  v-model="form.is_active"
                  :disabled="selectedNode.deletable == false"
                  :label="form.is_active ? $t('apps.accounting.message.active') : $t('apps.accounting.message.inactive')"
                />
              </div>
              <div class="col-12 mt-3">
                <div
                  :class="[
                    actionType == 'edit_account' && selectedNode.deletable == true
                      ? 'justify-content-between'
                      : 'justify-content-end'
                  ]"
                  class="d-flex align-items-center"
                >
                  <a
                    v-if="actionType == 'edit_account' && selectedNode.deletable == true"
                    class="text-danger"
                    style="cursor: pointer;"
                    @click="handleOpenConfirmDelete"
                  >
                    {{ $t('button.delete') }}
                  </a>
                  <BaseButton
                    :disabled="selectedNode.deletable == false"
                    class="btn-web btn-sm"
                    @click="handleSaveAccount"
                  >
                    {{ $t('button.save') }}
                  </BaseButton>
                </div>
              </div>
            </div>
          </div>
        </div>
      </b-modal>
      <baseConfirmDeleteModal
        :show="modalConfirmDelete.show"
        :loading="modalConfirmDelete.loading"
        @onClose="handleCloseConfirmDelete"
        @onCancel="handleCancleConfirmDelete"
        @onSubmit="handleSumitConfirmDelete"
      />
    </BaseContent>
  </BaseBody>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import { cloneDeep } from 'lodash'

import sidebarModule from './../../sidebarModule'
import TreeItem from '../../../custom/TreeItem.vue'
import { flattenChartOfAccounts, buildTree, getMasterDataText, setOpenedAccounts } from '@/helpers'
import { required } from 'vuelidate/lib/validators'
import userPermissionMixin from '@/mixins/userPermission'

export default {
  components: {
    TreeItem,
  },

  mixins: [userPermissionMixin],

  data () {
    return {
      loading: true,
      viewChartOfAccounts: [],
      manageChartOfAccounts: [],

      selectedNode: {},
      parentNode: {},
      actionType: null, // add_group, add_account, edit_account
      form: {
        parent_account_code: '',
        parent_account_name: '',
        account_id: '',
        account_type: '',
        account_code: '',
        account_name: '',
        is_group: false,
        is_active: false,
      },

      openedAccountCodes: [],

      modalConfirmDelete: {
        show: false,
        loading: false,
      },
    }
  },
  validations: {
    form: {
      account_code: {
        required,
      },
      account_name: {
        required,
      },
    },
  },
  computed: {
    ...mapGetters({
      chartOfAccounts: 'chartOfAccounts',
    }),
  },
  methods: {
    ...mapActions([
      'getChartOfAccounts',
      'insertChartOfAccounts',
      'updateChartOfAccounts',
      'deleteChartOfAccounts',
    ]),
    findAccountByCode(nodes, code) {
      for (const account of nodes) {
        if (account.account_code === code) {
          return account
        }
        if (account.children && account.children.length > 0) {
          const found = this.findAccountByCode(account.children, code)
          if (found) return found
        }
      }
      return null
    },
    getNodes () {
      const flatList = flattenChartOfAccounts(this.chartOfAccounts)
      const nodes = buildTree(flatList)
      return nodes
    },
    async fetchData() {
      await this.getChartOfAccounts({ params: {} })
      this.initData()
      this.showFinish(this.$Progress)
    },
    initData() {
      this.viewChartOfAccounts = cloneDeep(this.getNodes())
    },
    resetForm() {
      this.selectedNode = {}
      this.parentNode = {}
      this.actionType = ''
      this.form.parent_account_code = ''
      this.form.parent_account_name = ''
      this.form.account_id = ''
      this.form.account_type = ''
      this.form.account_code = ''
      this.form.account_name = ''
      this.form.is_group = false
      this.form.is_active = false
      this.$v.$reset()
    },
    openModalManageAccounts() {
      this.manageChartOfAccounts = cloneDeep(this.getNodes())
      this.resetForm()
      this.$bvModal.show('modal-manage-accounts')
    },
    closeModalManageAccounts() {
      this.$bvModal.hide('modal-manage-accounts')
    },
    handleClickNode(node) {
      if (node.level == 1) {
        return
      }
      const parentNode = this.findAccountByCode(this.manageChartOfAccounts, node.parent_account_code)
      this.selectedNode = node
      this.parentNode = parentNode
      this.actionType = 'edit_account'
      this.form.parent_account_code = node.parent_account_code
      this.form.parent_account_name = getMasterDataText(parentNode.account_name, this.$t)
      this.form.account_id = node._id
      this.form.account_type = parentNode.account_type
      this.form.account_code = node.account_code
      this.form.account_name = getMasterDataText(node.account_name, this.$t)
      this.form.is_group = node.is_group
      this.form.is_active = node.is_active

      this.$v.$reset()
    },
    handleAddGroup(node) {
      const parentNode = this.findAccountByCode(this.manageChartOfAccounts, node.parent_account_code)
      this.parentNode = parentNode
      this.actionType = 'add_group'
      this.form.parent_account_code = node.parent_account_code
      this.form.parent_account_name = getMasterDataText(parentNode.account_name, this.$t)
      this.form.account_id = ''
      this.form.account_type = parentNode.account_type
      this.form.account_code = ''
      this.form.account_name = ''
      this.form.is_group = true
      this.form.is_active = true

      this.$v.$reset()
    },
    handleAddAccount(node) {
      const parentNode = this.findAccountByCode(this.manageChartOfAccounts, node.parent_account_code)
      this.parentNode = parentNode
      this.actionType = 'add_account'
      this.form.parent_account_code = node.parent_account_code
      this.form.parent_account_name = getMasterDataText(parentNode.account_name, this.$t)
      this.form.account_id = ''
      this.form.account_type = parentNode.account_type
      this.form.account_code = ''
      this.form.account_name = ''
      this.form.is_group = false
      this.form.is_active = true

      this.$v.$reset()
    },
    async handleDeleteAccount() {
      const response = await this.deleteChartOfAccounts({
        accountId: this.form.account_id,
      })

      await this.fetchData()
      this.manageChartOfAccounts = cloneDeep(this.getNodes())
      for (const accountCode of this.openedAccountCodes) {
        setOpenedAccounts(this.manageChartOfAccounts, accountCode)
      }

      this.notify(this.$vs, {
        text: this.$t('message.hasbeendeleted', { msg: `${this.selectedNode.account_code} - ${this.selectedNode.account_name}` }),
        color: 'danger',
        icon: 'delete',
      })

      this.resetForm()

      this.handleCloseConfirmDelete()
    },
    async insertNewAccount() {
      const response = await this.insertChartOfAccounts({
        data: {
          'account_code': this.form.account_code,
          'account_name': this.form.account_name,
          'account_type': this.form.account_type,
          'is_group': this.form.is_group,
          'parent_account_code': this.form.parent_account_code,
        },
      })

      await this.fetchData()
      this.manageChartOfAccounts = cloneDeep(this.getNodes())
      for (const accountCode of this.openedAccountCodes) {
        setOpenedAccounts(this.manageChartOfAccounts, accountCode)
      }

      this.notify(this.$vs, {
        text: this.$t('message.hasbeencreated', { msg: `${this.form.account_code} - ${this.form.account_name}` }),
        color: 'success',
        icon: 'cloud',
      })

      this.resetForm()
    },
    async updateAccount() {
      const response = await this.updateChartOfAccounts({
        accountId: this.form.account_id,
        data: {
          'account_code': this.form.account_code,
          'account_name': this.form.account_name,
          'is_active': this.form.is_active,
        },
      })

      await this.fetchData()
      this.manageChartOfAccounts = cloneDeep(this.getNodes())
      for (const accountCode of this.openedAccountCodes) {
        setOpenedAccounts(this.manageChartOfAccounts, accountCode)
      }

      this.notify(this.$vs, {
        text: this.$t('message.hasbeenupdated', { msg: `${this.form.account_code} - ${this.form.account_name}` }),
        color: 'success',
        icon: 'cloud',
      })

      this.resetForm()
    },
    async handleSaveAccount() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        return false
      }

      if (this.actionType == 'add_group' || this.actionType == 'add_account') {
        await this.insertNewAccount()
      } else if (this.actionType == 'edit_account') {
        await this.updateAccount()
      }
    },
    handleToggleNode(node) {
      if (this.openedAccountCodes.includes(node.account_code)) {
        this.openedAccountCodes = this.openedAccountCodes.filter((code) => code != node.account_code)
      } else {
        this.openedAccountCodes.push(node.account_code)
      }
    },
    handleOpenConfirmDelete () {
      this.modalConfirmDelete.show = true
    },
    handleCloseConfirmDelete() {
      this.modalConfirmDelete.show = false
    },
    handleCancleConfirmDelete() {
      this.modalConfirmDelete.show = false
    },
    async handleSumitConfirmDelete() {
      this.modalConfirmDelete.loading = true
      await this.handleDeleteAccount()
      this.modalConfirmDelete.loading = false
    },
  },
  async created () {
    await this.fetchData()
    this.loading = false
  },
  beforeMount: function () {
    // init page
    this.initPage({
      section: 'app',
      name: this.$t('app.accounts.title'),
      current: {
        app_name: 'accounts',
        app_id: 12,
        module_id: 7,
      },
    })
    // set sidebar ui
    sidebarModule.init(this.getObject(require('../sidebar.json')), {
      sidebarAtIdx: 5,
    })
  },
  mounted () {

  },
  destroyed () {
    this.leavePage(this.$Progress, [])
  },
}
</script>

<style scoped>
.form {
  border-left: 1px solid #eff1f2;
}
</style>



// WEBPACK FOOTER //
// src/components/apps/accounts/chartofaccount/Index.vue